// client/src/components/Parent/AnalyticsPanel.js
//
// The Analytics tab, which until now said "coming soon".
//
// EVERY FIGURE HERE IS DERIVED FROM MARKS ALREADY ON THE PAGE. No new endpoint,
// nothing the school has to configure, and nothing a parent could not check by
// hand against the sequence table on the tab beside this one. That constraint is
// the point: an analytics panel that invents a metric nobody can reproduce is
// worse than an empty one, because a parent cannot tell a real problem from a
// bug in the chart.
//
// So it answers four questions and stops:
//   Which way is it going?   the trend across sequences, which no single
//                            sequence can show
//   How is it going now?     subjects at or above the pass mark in the latest
//                            sequence
//   What is strong or weak?  subjects ranked by their own average
//   Anything else pending?   attendance and fees, already computed elsewhere
//
// The arithmetic is in parentAverages.generated.js, generated from packages/core
// so the phone reports the same numbers. Nothing here does maths beyond
// formatting.

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Card, CardContent, Chip, Grid, LinearProgress, Table, TableBody,
  TableCell, TableHead, TableRow, Typography, useTheme,
} from '@mui/material';
import {
  TrendingUp as UpIcon, TrendingDown as DownIcon, TrendingFlat as FlatIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ReferenceLine,
} from 'recharts';
import { performanceInsights, gradeFor, DEFAULT_PASS_MARK } from '../../utils/parentAverages.generated';

const fmt = (n) => (n == null || !Number.isFinite(Number(n)) ? '—' : Number(n).toFixed(2));

function StatCard({ label, value, caption, colour }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={colour ? { color: colour } : undefined}>{value}</Typography>
        {caption ? (
          <Typography variant="caption" color="text.secondary">{caption}</Typography>
        ) : null}
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPanel({ subjects, marks, grading, attendanceRate, fees }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const passMark = Number(grading?.passMark) || DEFAULT_PASS_MARK;
  const scale = Array.isArray(grading?.scale) ? grading.scale : [];

  const insights = useMemo(() => performanceInsights(subjects, marks, {
    subjects,
    passMark,
    termsPerYear: grading?.termsPerYear,
    sequencesPerTerm: grading?.sequencesPerTerm,
  }), [subjects, marks, passMark, grading?.termsPerYear, grading?.sequencesPerTerm]);

  if (!insights.hasAny) {
    return (
      <Alert severity="info">
        {t('parentAnalytics.nothingYet', {
          defaultValue: 'Nothing to analyse yet — this fills in as marks are published.',
        })}
      </Alert>
    );
  }

  const { movement } = insights;
  // Movement is null with one sequence: there is nothing to compare against.
  // Zero would read as "no progress", which is a different claim.
  const MovementIcon = movement == null ? FlatIcon : (movement >= 0.05 ? UpIcon : (movement <= -0.05 ? DownIcon : FlatIcon));
  const movementColour = movement == null || Math.abs(movement) < 0.05
    ? theme.palette.text.secondary
    : (movement > 0 ? theme.palette.success.main : theme.palette.error.main);

  const chartData = insights.trend.map((p) => ({
    name: t('parentAcademics.sequenceShort', { number: p.sequenceNumber }),
    average: Number(Number(p.average).toFixed(2)),
  }));

  const passRate = insights.graded
    ? Math.round((insights.passing / insights.graded) * 100)
    : null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label={t('parentAnalytics.movement', { defaultValue: 'Change since last sequence' })}
          value={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <MovementIcon fontSize="inherit" />
              {movement == null ? '—' : `${movement > 0 ? '+' : ''}${movement.toFixed(2)}`}
            </Box>
          )}
          caption={movement == null
            ? t('parentAnalytics.needsTwo', { defaultValue: 'Needs a second sequence' })
            : undefined}
          colour={movementColour}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label={t('parentAnalytics.passing', { defaultValue: 'Subjects passed' })}
          value={insights.graded ? `${insights.passing}/${insights.graded}` : '—'}
          caption={insights.latestSequence != null
            ? t('parentAcademics.sequenceShort', { number: insights.latestSequence })
            : undefined}
          colour={passRate == null ? undefined
            : (passRate >= 50 ? theme.palette.success.main : theme.palette.error.main)}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label={t('parentDashboard.quickStats.attendanceRate')}
          value={attendanceRate != null ? `${Number(attendanceRate).toFixed(1)}%` : '—'}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label={t('parentDashboard.summary.outstanding')}
          value={Number(fees?.outstanding || 0).toLocaleString()}
          caption={Number(fees?.outstanding || 0) > 0
            ? t('parentDashboard.status.stillOwing')
            : t('parentDashboard.status.complete')}
          colour={Number(fees?.outstanding || 0) > 0
            ? theme.palette.warning.main
            : theme.palette.success.main}
        />
      </Grid>

      {/* THE TREND. One sequence is a snapshot; the shape across the year is the
          thing a parent cannot get anywhere else in this portal. The pass mark
          is drawn on it so a point is read against the line that matters rather
          than against the axis. */}
      <Grid item xs={12} md={7}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              {t('parentAnalytics.trend', { defaultValue: 'Average by sequence' })}
            </Typography>
            {chartData.length < 2 ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('parentAnalytics.needsTwo', { defaultValue: 'Needs a second sequence' })}
              </Alert>
            ) : (
              <Box sx={{ width: '100%', height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 20]} tick={{ fontSize: 12 }} />
                    <RechartsTooltip />
                    <ReferenceLine
                      y={passMark}
                      stroke={theme.palette.warning.main}
                      strokeDasharray="4 4"
                      label={{ value: t('parentAnalytics.passMark', { defaultValue: 'Pass' }), fontSize: 11 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="average"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={5}>
        <Card variant="outlined" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              {t('parentAnalytics.standing', { defaultValue: 'Strongest and weakest' })}
            </Typography>
            {insights.strongest ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('parentAnalytics.strongest', { defaultValue: 'Strongest' })}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {insights.strongest.name} — {fmt(insights.strongest.average)}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    color="success"
                    value={Math.min(100, (Number(insights.strongest.average) / 20) * 100)}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                {/* Only when there is more than one graded subject: with one, the
                    strongest and the weakest are the same row and printing it
                    twice reads as a bug. */}
                {insights.weakest && insights.weakest.id !== insights.strongest.id && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('parentAnalytics.weakest', { defaultValue: 'Needs attention' })}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {insights.weakest.name} — {fmt(insights.weakest.average)}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      color={Number(insights.weakest.average) >= passMark ? 'warning' : 'error'}
                      value={Math.min(100, (Number(insights.weakest.average) / 20) * 100)}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Alert severity="info">{t('parentAcademics.noSequenceMarks')}</Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              {t('parentAnalytics.bySubject', { defaultValue: 'Every subject, best first' })}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('parentAcademics.columns.subjects')}</TableCell>
                  <TableCell align="right">{t('parentAcademics.columns.coefficient')}</TableCell>
                  <TableCell align="right">{t('parentAcademics.average')}</TableCell>
                  <TableCell>{t('parentAcademics.columns.grade')}</TableCell>
                  <TableCell align="right">{t('parentAnalytics.marksCounted', { defaultValue: 'Marks' })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {insights.bySubject.map((row) => {
                  const band = row.average == null ? null : gradeFor(row.average, scale);
                  const failed = row.average != null && row.average < passMark;
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{row.name}</Typography>
                          {row.code ? <Chip size="small" label={row.code} /> : null}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{row.coefficient}</TableCell>
                      <TableCell align="right" sx={failed ? { color: 'error.main', fontWeight: 600 } : undefined}>
                        {fmt(row.average)}
                      </TableCell>
                      <TableCell>{band?.grade || '—'}</TableCell>
                      <TableCell align="right">{row.marks || 0}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
